/**
 * focus-timer-derive.ts — Pure, testable logic for the D35 focus timer.
 * No React, no side effects, no DOM access.
 * All functions are deterministic given their inputs.
 */

// ── Core computation ──────────────────────────────────────────────────────────

/**
 * Calculate remaining seconds for a focus timer session.
 *
 * @param startedAtMs    - ms timestamp when the timer started (or last resumed after pause)
 * @param durationMin    - total planned duration in minutes
 * @param pausedAccumMs  - total accumulated pause duration in ms (sum of all past pauses)
 * @param nowMs          - current time in ms (inject for testability)
 * @param pausedAt       - if currently paused: ms timestamp when pause began; absent = running
 * @returns              remaining seconds (clamped to 0 — never negative)
 */
export function remainingSeconds(
  startedAtMs: number,
  durationMin: number,
  pausedAccumMs: number,
  nowMs: number,
  pausedAt?: number,
): number {
  const totalMs = durationMin * 60 * 1000;
  // When paused, elapsed stops advancing — use pausedAt as the "frozen" now.
  const effectiveNow = pausedAt != null ? pausedAt : nowMs;
  const elapsedMs = effectiveNow - startedAtMs - pausedAccumMs;
  const remainingMs = totalMs - elapsedMs;
  return Math.max(0, Math.floor(remainingMs / 1000));
}

// ── Formatting ────────────────────────────────────────────────────────────────

/**
 * Format a seconds count as "MM:SS" (e.g. 90 → "01:30").
 * Works correctly for values >= 60 minutes (e.g. 3600 → "60:00").
 */
export function formatMmSs(totalSeconds: number): string {
  const secs = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

// ── Progress ──────────────────────────────────────────────────────────────────

/**
 * Returns progress as a 0–100 percentage of time elapsed.
 * 0 = just started, 100 = expired.
 */
export function timerProgressPercent(
  startedAtMs: number,
  durationMin: number,
  pausedAccumMs: number,
  nowMs: number,
  pausedAt?: number,
): number {
  const totalSecs = durationMin * 60;
  if (totalSecs <= 0) return 100;
  const remaining = remainingSeconds(startedAtMs, durationMin, pausedAccumMs, nowMs, pausedAt);
  return Math.round(((totalSecs - remaining) / totalSecs) * 100);
}

// ── Elapsed minutes ───────────────────────────────────────────────────────────

/**
 * Compute focus minutes actually worked in the current session.
 * elapsedSec = (durationMin * 60) − remaining; elapsedMin = round(elapsedSec / 60).
 * Returns 0 when the timer has not advanced at all.
 *
 * @param startedAtMs   - ms timestamp when the timer started
 * @param durationMin   - planned duration in minutes
 * @param pausedAccumMs - accumulated pause duration in ms
 * @param nowMs         - current time in ms (inject for testability)
 * @param pausedAt      - if currently paused: ms timestamp when pause began; absent = running
 * @returns             elapsed minutes, clamped to [0, durationMin]
 */
export function elapsedFocusMinutes(
  startedAtMs: number,
  durationMin: number,
  pausedAccumMs: number,
  nowMs: number,
  pausedAt?: number,
): number {
  const totalSecs = durationMin * 60;
  const remSecs = remainingSeconds(startedAtMs, durationMin, pausedAccumMs, nowMs, pausedAt);
  const elapsedSecs = totalSecs - remSecs;
  return Math.max(0, Math.round(elapsedSecs / 60));
}

// ── Default duration ──────────────────────────────────────────────────────────

/** Fallback focus duration when a task has no estimateMinutes. */
export const DEFAULT_FOCUS_MINUTES = 25;

/**
 * Resolve the duration to use for a focus session.
 * Returns the task's estimateMinutes when > 0, otherwise DEFAULT_FOCUS_MINUTES.
 */
export function resolveDurationMin(estimateMinutes: number | undefined): number {
  if (estimateMinutes != null && estimateMinutes > 0) return estimateMinutes;
  return DEFAULT_FOCUS_MINUTES;
}
